import { Destination } from '../types/catalog'
import { zipCentroids } from '../data/zipCentroids'

export type GeocodeMethod = 'zip' | 'city' | 'address'

export interface GeocodeHit extends Destination {
  /** How the coordinates were found — drives the precision note in the UI. */
  method: GeocodeMethod
}

const ZIP_PATTERN = /\b\d{5}\b/

/**
 * Instant, offline lookup. Matches a 5-digit ZIP first, then a city name
 * from the ZIP table. Returns null when the query needs the live geocoder.
 */
export function resolveLocally(query: string): GeocodeHit | null {
  const trimmed = query.trim()
  if (!trimmed) return null
  const lower = trimmed.toLowerCase()

  const zipMatch = trimmed.match(ZIP_PATTERN)
  if (zipMatch) {
    const record = zipCentroids.find((entry) => entry.zip === zipMatch[0])
    if (record) {
      return {
        label: `${record.city} ${record.zip}`,
        zip: record.zip,
        coords: record.coords,
        method: 'zip',
      }
    }
  }

  // Only treat it as a city match when there's no street number in the query —
  // "1200 Kent Ave" should geocode properly, not snap to Kent's centroid.
  if (/^\s*\d+\s/.test(trimmed)) return null

  const record = zipCentroids.find((entry) => {
    const city = entry.city.replace(/\s*\(.*\)\s*/, '').toLowerCase()
    return lower === city || lower.startsWith(`${city},`) || lower === `${city} wa`
  })
  if (!record) return null

  return {
    label: `${record.city}, WA`,
    zip: record.zip,
    coords: record.coords,
    method: 'city',
  }
}

/** Detects a query specific enough to be worth a live street-level lookup. */
export function looksLikeStreetAddress(query: string): boolean {
  return /^\s*\d+\s+\S+/.test(query.trim())
}

interface NominatimResult {
  lat: string
  lon: string
  display_name: string
  address?: { postcode?: string }
}

/**
 * Street-level geocoding via OpenStreetMap Nominatim. Biased to the
 * Puget Sound viewbox so partial addresses resolve locally.
 */
export async function geocodeAddress(
  query: string,
  signal?: AbortSignal,
): Promise<GeocodeHit | null> {
  const params = new URLSearchParams({
    q: query.trim(),
    format: 'jsonv2',
    addressdetails: '1',
    limit: '1',
    countrycodes: 'us',
    viewbox: '-123.2,48.4,-121.4,46.9',
    bounded: '1',
  })

  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    signal,
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(`Geocoder returned ${response.status}`)

  const results = (await response.json()) as NominatimResult[]
  const hit = results[0]
  if (!hit) return null

  const parts = hit.display_name.split(',').map((part) => part.trim())
  return {
    label: parts.slice(0, 3).join(', '),
    zip: hit.address?.postcode ?? '',
    coords: { lat: Number.parseFloat(hit.lat), lng: Number.parseFloat(hit.lon) },
    method: 'address',
  }
}
